/* eslint-disable testing-library/no-debugging-utils */
import {
  createPlaidLinkTokenV2,
  LinkedAccountStatus,
  type LinkPlaidAchAccountInput,
  useCustomerDetails,
  useLinkPlaidAchAccount,
} from '@egw/api'
import {
  Alert,
  AlertType,
  Button,
  ButtonVariant,
  IconConfirmationCircle,
  IconErrorCircle,
  Input,
} from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { produce } from 'immer'
import { type FC, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  type PlaidLinkError,
  type PlaidLinkOnEvent,
  type PlaidLinkOnEventMetadata,
  type PlaidLinkOnExit,
  type PlaidLinkOnExitMetadata,
  type PlaidLinkOnSuccess,
  type PlaidLinkOnSuccessMetadata,
  usePlaidLink,
} from 'react-plaid-link'
import { Loader } from '../loader/loader'

const log = new Logger('Instant Linking - Plaid')

interface FormData {
  name: string
}

export interface PlaidLinkProps {
  isRelinking?: boolean
  onPlaidSuccess?: () => void
  onPlaidExit?: () => void
  onClose: () => void
}

export const PlaidLink: FC<PlaidLinkProps> = ({ isRelinking, onPlaidSuccess, onPlaidExit, onClose }) => {
  const [isCreatingLinkToken, setIsCreatingLinkToken] = useState(false)
  const [isCreatingLinkTokenError, setIsCreatingLinkTokenError] = useState(false)
  const [linkingError, setLinkingError] = useState(false)
  const [linkingSuccess, setLinkingSuccess] = useState(false)
  const [step, setStep] = useState(0)
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [linkPlaidAchAccountInput, setLinkPlaidAchAccountInput] = useState<LinkPlaidAchAccountInput>()

  const { t } = useTranslation('features-external-accounts')

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    setValue,
  } = useForm<FormData>({
    mode: 'onTouched',
  })

  const {
    mutate: linkPlaidAchAccount,
    isLoading: isLinkPlaidAchAccountLoading,
    error: linkPlaidAchAccountError,
    data: linkPlaidAchAccountData,
  } = useLinkPlaidAchAccount()

  const { refetch } = useCustomerDetails({ enabled: false })

  // Create Plaid Link token on component mount
  useEffect(() => {
    const createLinkToken = async (): Promise<void> => {
      setIsCreatingLinkToken(true)
      try {
        const linkTokenResponse = await createPlaidLinkTokenV2({})
        if (linkTokenResponse.__typename === 'PlaidLinkToken') {
          setLinkToken(linkTokenResponse.linkToken)
        } else {
          sendEvent('eg_linkInstantly_error', {
            eventSource: 'egWealth',
            category: 'instant-link',
            subCategory: 'result',
            error: linkTokenResponse,
          })
          setIsCreatingLinkTokenError(true)
        }
      } catch (error) {
        log.error('Error creating Plaid Link token: ', error)
        sendEvent('eg_linkInstantly_error', {
          eventSource: 'egWealth',
          category: 'instant-link',
          subCategory: 'result',
          error,
        })
        setIsCreatingLinkTokenError(true)
      } finally {
        setIsCreatingLinkToken(false)
      }
    }
    void createLinkToken()
  }, [])

  const handlePlaidLinkSuccess: PlaidLinkOnSuccess = useCallback(
    (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      log.debug('Plaid linking success')
      onPlaidSuccess?.()
      setLinkPlaidAchAccountInput({
        publicToken,
        accountName: metadata.accounts[0].name,
        metadata: {
          institutionId: metadata.institution?.institution_id ?? '',
          institutionName: metadata.institution?.name ?? '',
          accounts: [
            {
              accountId: metadata.accounts[0].id,
              name: metadata.accounts[0].name,
              mask: metadata.accounts[0].mask,
              type: metadata.accounts[0].type,
              subtype: metadata.accounts[0].subtype,
            },
          ],
        },
      })
      setStep(1)
      setValue('name', metadata.accounts[0].name, {
        shouldValidate: true,
      })
      // SEND PLAID LINK ON SUCCESSFUL EVENT HERE
      sendEvent('eg_plaidLink_onSuccess', {
        eventSource: 'egWealth',
        category: 'instant-link',
        subCategory: 'plaid-linking',
        plaidMetaData: metadata,
      })
    },
    [onPlaidSuccess, setLinkPlaidAchAccountInput, setValue],
  )

  const handlePlaidLinkExit: PlaidLinkOnExit = useCallback(
    (error: PlaidLinkError | null, plaidMetaData: PlaidLinkOnExitMetadata) => {
      log.debug('Plaid linking exit', { error, plaidMetaData })
      onPlaidExit?.()
      sendEvent('eg_plaidLink_onExit', {
        eventSource: 'egWealth',
        category: 'instant-link',
        subCategory: 'plaid-linking',
        plaidMetaData,
      })
    },
    [],
  )

  const handlePlaidLinkEvent: PlaidLinkOnEvent = useCallback((eventName, plaidMetaData: PlaidLinkOnEventMetadata) => {
    log.debug('Plaid linking event', { eventName, plaidMetaData })
    sendEvent('eg_plaidLink_onEvent', {
      eventSource: 'egWealth',
      category: 'instant-link',
      subCategory: 'plaid-linking',
      plaidMetaData,
    })
  }, [])

  const { open, ready } = usePlaidLink({
    onSuccess: handlePlaidLinkSuccess,
    onExit: handlePlaidLinkExit,
    onEvent: handlePlaidLinkEvent,
    token: linkToken,
  })

  const onSubmit = handleSubmit(async (formData: unknown): Promise<void> => {
    setStep(2)
    const d = formData as FormData
    try {
      if (linkPlaidAchAccountInput) {
        const res = await linkPlaidAchAccount(
          produce(linkPlaidAchAccountInput, (draft) => {
            draft.accountName = d.name
          }),
        )
        if (res?.__typename === 'LinkedAchAccount' && res.linkedAccountStatus === LinkedAccountStatus.ACTIVE) {
          setLinkingSuccess(true)
          sendEvent('eg_linkInstantly_success', {
            eventSource: 'egWealth',
            category: 'instant-link',
            subCategory: 'result',
            accountType: linkPlaidAchAccountInput.metadata.accounts[0].type,
            nickname: d.name,
          })
          const isSingleLinked = await refetch()
          if (isSingleLinked.data?.customer.linkedAccounts.isSingleRestricted) {
            sendEvent('eg_firstAccount_link', {
              eventSource: 'egWealth',
              category: 'account-linking',
              subCategory: 'result',
            })
          }
        } else {
          setLinkingError(true)
          sendEvent('eg_linkInstantly_fail', {
            eventSource: 'egWealth',
            category: 'instant-link',
            subCategory: 'result',
            failureReason: res?.__typename === 'LinkedAchAccount' ? res.linkedAccountStatus : res?.errorType,
          })
        }
      }
    } catch (error) {
      log.error(error)
      setLinkingError(true)
      sendEvent('eg_linkInstantly_error', {
        eventSource: 'egWealth',
        category: 'instant-link',
        subCategory: 'result',
        error,
      })
    }
  })

  return (
    <>
      {/* Instant or Manual linking screen */}
      {step === 0 && (
        <>
          <p className="font-semibold mb-3xs mt-xs">
            {isRelinking ? t('accounts.relink.title') : t('accounts.instant.title')}
          </p>
          <p className="mb-xs text-body">
            {isRelinking ? t('accounts.relink.message') : t('accounts.instant.message')}
          </p>
          {isCreatingLinkTokenError ? (
            <Alert
              alertType={AlertType.Error}
              title={t('plaid_link.token_error_header')}
              description={t('plaid_link.token_error_message')}
              className="mb-2xs"
            />
          ) : null}
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => {
              sendEvent('user_linkInstantly_click', {
                eventSource: 'egWealth',
                category: 'instant-link',
                subCategory: 'intent',
              })
              open()
            }}
            testId="add_account_link_instantly_secondary_btn"
            id="add_account_link_instantly_secondary_btn"
            isLoading={isCreatingLinkToken}
            isDisabled={isCreatingLinkTokenError || !ready}
          >
            {isRelinking ? t('accounts.relink.cta') : t('accounts.instant.cta')}
          </Button>
        </>
      )}

      {/* Update nickname form */}
      {step === 1 && (
        <form onSubmit={(e) => void onSubmit(e)}>
          <div className="mb-4">
            <Controller
              name="name"
              control={control}
              rules={{ required: t('plaid_link.account_nickname_required') }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('details.update_name_label')}
                  id="addNickname"
                  testId="addNickname_input"
                  required
                  error={formErrors.name?.message?.toString()}
                />
              )}
            />
          </div>

          <Button
            variant={ButtonVariant.Primary}
            type="submit"
            className="w-full"
            onClick={(e) => void onSubmit(e)}
            testId="add_account_error_cancel_primary_btn"
            isDisabled={!isValid}
          >
            {t('plaid_link.continue')}
          </Button>
        </form>
      )}

      {/* Loading, error, success states for saving a new account */}
      {step === 2 && (
        <>
          {isLinkPlaidAchAccountLoading ? (
            <>
              <Loader className="my-lg" />
              <p className="font-light text-header text-xl mb-2xs">{t('plaid_link.loading_title')}</p>
              <p>{t('plaid_link.loading_message')}</p>
            </>
          ) : null}

          {linkingSuccess ? (
            <>
              <IconConfirmationCircle className="mt-lg mb-sm" />
              <p className="mb-xs">
                {t('plaid_link.account_verified', {
                  accountName:
                    linkPlaidAchAccountData?.__typename === 'LinkedAchAccount' ? linkPlaidAchAccountData.name : '',
                })}
              </p>
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => {
                  onClose()
                }}
                className="mb-2xs"
              >
                {t('plaid_link.continue')}
              </Button>
            </>
          ) : null}

          {(linkPlaidAchAccountError ?? linkingError) ? (
            <>
              <IconErrorCircle className="my-lg" />
              <p className="mb-xs">{t('plaid_link.error_p1')}</p>
              <p className="mb-xs">{t('plaid_link.error_p2')}</p>
              <Button
                variant={ButtonVariant.Secondary}
                onClick={() => {
                  sendEvent('eg_linkInstantly_fail', {
                    eventSource: 'egWealth',
                    category: 'instant-link',
                    subCategory: 'result',
                    failureReason: 'cancelled',
                  })
                  onClose()
                }}
                className="mb-2xs"
              >
                {t('plaid_link.cancel')}
              </Button>
            </>
          ) : null}
        </>
      )}
    </>
  )
}
